import { createContext, useContext, useEffect, useState } from "react";
import * as React from "react";

interface NotificationContextType {
  isNotificationDrawerOpen: boolean;
  openNotificationDrawer: () => void;
  closeNotificationDrawer: () => void;
  unreadCount: number;
  setUnreadCount: (count: number) => void;
}

type NotificationProviderProps = {
  children: React.ReactNode;
};

const NotificationContext = createContext<NotificationContextType>({
  closeNotificationDrawer: () => {},
  isNotificationDrawerOpen: false,
  openNotificationDrawer: () => {},
  setUnreadCount: () => {},
  unreadCount: 0
});

const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] =
    useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const openNotificationDrawer = () => setIsNotificationDrawerOpen(true);
  const closeNotificationDrawer = () => setIsNotificationDrawerOpen(false);

  // Mirror the unread count onto the installed app's icon badge (dock/taskbar).
  // Only shows while a window is open; Safari desktop lacks the API and no-ops.
  useEffect(() => {
    if (!("setAppBadge" in navigator)) return;
    if (unreadCount > 0) {
      navigator.setAppBadge(unreadCount).catch(() => {});
    } else {
      navigator.clearAppBadge?.().catch(() => {});
    }
  }, [unreadCount]);

  return (
    <NotificationContext.Provider
      value={{
        closeNotificationDrawer,
        isNotificationDrawerOpen,
        openNotificationDrawer,
        setUnreadCount,
        unreadCount
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

const useNotificationContext = () => useContext(NotificationContext);

export { NotificationProvider, useNotificationContext };
